/**
 * src/services/api.ts
 * 
 * Centralized API client for all backend communication.
 * Supports both Vercel (temporary) and Spring Boot (production) endpoints.
 */

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';

interface ApiRequestInit extends RequestInit {
  headers?: Record<string, string>;
}

/**
 * Make a fetch request to the backend
 */
export async function apiRequest<T>(
  endpoint: string,
  options: ApiRequestInit = {}
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;
  
  try {
    const response = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    });

    // Try to parse JSON error response
    let errorData: any = {};
    try {
      errorData = await response.clone().json();
    } catch {
      // Response is not JSON, that's fine
    }

    if (!response.ok) {
      const errorMessage = 
        errorData?.message || 
        errorData?.error || 
        `HTTP ${response.status}`;
      
      throw new Error(errorMessage);
    }

    return response.json();
  } catch (err) {
    // Network errors or JSON parsing errors
    if (err instanceof TypeError) {
      throw new Error('Network error. Please check your internet connection and try again.');
    }
    throw err;
  }
}

/**
 * POST to /checkout endpoint
 * 
 * Routes to Node.js backend: POST /checkout
 */
export async function createCheckout(data: unknown): Promise<{ clientSecret: string; free?: boolean; orderId?: string }> {
  return apiRequest('/checkout', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * POST to /validate-coupon endpoint
 * Validates a coupon code before checkout submission
 */
export async function validateCoupon(couponCode: string): Promise<{ valid: boolean; description?: string; message?: string; freeShipping?: boolean; percentOff?: number | null; overrideTotalCents?: number | null }> {
  return apiRequest('/api/validate-coupon', {
    method: 'POST',
    body: JSON.stringify({ couponCode }),
  });
}

/**
 * POST to /webhooks/stripe endpoint (Spring Boot only)
 */
export async function notifyWebhook(event: unknown): Promise<void> {
  return apiRequest('/webhooks/stripe', {
    method: 'POST',
    body: JSON.stringify(event),
  });
}

export default {
  apiRequest,
  createCheckout,
  notifyWebhook,
};
